import { m } from '@pricedrop/i18n';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { buildCanonicalLink, buildJsonLd, buildMeta } from '@/lib/seo';
import { ssrForPublicOnly } from '@/lib/ssr-utils';

export const Route = createFileRoute('/faq/')({
  ssr: ssrForPublicOnly,
  head: () => ({
    meta: buildMeta({
      title: 'שאלות נפוצות - Pricedrop | מעקב מחירים KSP',
      description:
        'תשובות לשאלות נפוצות על Pricedrop - איך עוקבים אחרי מחירים, שימוש בתוסף, דיוק הנתונים ועוד.',
      canonicalUrl: '/faq',
    }),
    links: [buildCanonicalLink('/faq')],
    scripts: [
      buildJsonLd({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.a,
          },
        })),
      }),
    ],
  }),
  component: FaqPage,
});

const items = [
  {
    q: 'מה זה Pricedrop?',
    a: 'Pricedrop עוקב ושומר אחרי היסטוריית המחירים של כל מוצרי KSP, אתר המסחר מהגדולים בישראל. בעזרת האתר תוכלו לדעת אם מחיר המוצר שאתם רוצים לרכוש הוא באמת אטרקטיבי או שניתן לרכוש אותו במחיר זול יותר.',
  },
  {
    q: 'אחרי איזה מוצרים אתם עוקבים?',
    a: 'כל מוצר המופיע באתר KSP נכנס למאגר של Pricedrop ומתבצע מעקב אחרי המחיר שלו.',
  },
  {
    q: 'האם המחירים המוצגים באתר מדוייקים?',
    a: 'המידע באתר מתעדכן לעיתים תכופות, אך מחירים באתר KSP משתנים כל הזמן ולכן יכול להיווצר מצב שהמחיר ב Pricedrop לא הספיק להתעדכן. אנחנו ממליצים לוודא שוב באתר KSP את מחיר המוצר הסופי לפני הרכישה.',
  },
  {
    q: 'מצאתי מוצר שמופיע בהנחה מוגזמת, האם זאת טעות?',
    a: 'מוצרים מסויימים מופיעים באתר KSP עם מחירים שרירותיים ורק לאחר זמן מתעדכן המחיר האמיתי, ולכן לפעמים יופיעו מוצרים בהנחה מוגזמת',
  },
  {
    q: 'האם האתר קשור ל KSP?',
    a: 'Pricedrop הנו מיזם עצמאי ואינו מופעל ע"י KSP.',
  },
  {
    q: 'האם המחיר הכי נמוך שמופיע למוצר באתר הוא בהכרח המחיר הכי נמוך שבו הוא נמכר?',
    a: 'האתר התחיל את לעקוב אחר מחירי במהלך אוקטובר 2020 ולכן אם היה מחיר נמוך יותר במועד מוקדם יותר לא ניתן לדעת.',
  },
];

function FaqPage() {
  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-12">
        <div className="mb-12 text-center">
          <h1 className="mb-4 text-4xl font-bold tracking-tight md:text-6xl">שאלות נפוצות</h1>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            תשובות לשאלות הנפוצות ביותר על Pricedrop
          </p>
        </div>

        <div className="mx-auto max-w-4xl">
          <div className="rounded-2xl bg-muted/50 p-8 shadow-xl backdrop-blur-sm">
            <Accordion type="multiple" className="space-y-4">
              {items.map((item) => (
                <AccordionItem
                  key={item.q}
                  value={item.q}
                  className="overflow-hidden rounded-xl border bg-card/70 transition-all duration-200 hover:shadow-md"
                >
                  <AccordionTrigger className="w-full px-6 py-4 text-start text-lg font-semibold transition-colors hover:text-primary">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="leading-relaxed text-muted-foreground">{item.a}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        <div className="mt-16 text-center">
          <div className="rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 p-8 text-white shadow-xl">
            <h3 className="mb-4 text-2xl font-bold">{m.faq_notFoundAnswer()}</h3>
            <p className="mb-6 text-blue-100">{m.faq_contactPrompt()}</p>
            <Link
              to="/contact"
              className="inline-flex items-center rounded-lg bg-white px-6 py-3 font-semibold text-blue-600 transition-all hover:bg-gray-100 hover:shadow-lg"
            >
              {m.common_contactUs()}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
